import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetNotesTreeQuery } from "../../app/services/notes";
import { useGetLibraryTreeQuery } from "../../app/services/library";
import useProjectDmAccess from "../../hooks/useProjectDmAccess";
import {
  PROJECT_NAV_ITEMS,
  canShowLibrary,
  canShowNotes,
  getVisibleProjectNavItemIds,
  type ProjectNavItem,
} from "./projectNavItems";

export interface UseProjectNavItemsArgs {
  projectId: string;
  /** ポジション文字列（一覧 API の `current_user_role` 等、呼び出し側で既知の値） */
  position: string;
  /** 納品物なし（total_shots_count === 0）。ホームを出さない */
  noDeliverable: boolean;
}

/**
 * プロジェクト外（横断タスクボード等）から、そのプロジェクトのナビアイテム
 * （ホーム / トークルーム / DM / ノート / ライブラリ）を下部バーと同じ基準で返す。
 *
 * 下部バー（BottomAppBarProject）は URL の projectId 前提の hook 構成のため、
 * こちらは projectId / position を引数で受ける。ノート / ライブラリ / DM の
 * 判定クエリは下部バー・パネル本体と同一のため RTK Query キャッシュを共有する。
 *
 * SPECIALIST で DM しか無い場合も DM アイコンは返す（下部バーでバーごと隠すのは
 * 「既にプロジェクト内にいる」ためで、プロジェクト外からは導線として必要）。
 */
export const useProjectNavItems = ({
  projectId,
  position,
  noDeliverable,
}: UseProjectNavItemsArgs): ProjectNavItem[] => {
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: notesTreeData } = useGetNotesTreeQuery(
    { project_id: projectId },
    { skip: !projectId || !currentUserData },
  );
  const { data: libraryTreeData } = useGetLibraryTreeQuery(
    { project_id: projectId },
    { skip: !projectId || !currentUserData },
  );
  // position は既知なのでメンバーシップ API は呼ばない（REVIEWER のみ
  // 参加スレッドの有無を 1 件だけ取得する）。
  const { canAccessDm } = useProjectDmAccess(projectId, { position });

  const visibleIds = getVisibleProjectNavItemIds({
    position,
    noDeliverable,
    canAccessDm,
    showNotes: canShowNotes(notesTreeData?.data),
    showLibrary: canShowLibrary(libraryTreeData?.data),
  });
  return PROJECT_NAV_ITEMS.filter((item) => visibleIds.includes(item.id));
};

export default useProjectNavItems;
